import { resolveAssistantArtifactSupport } from "../../shared/assistant-artifact-support";
import {
  ADMISSIONS_THREEJS_VISUALIZATION_PROMPT,
  TUITION_PRIOR_YEAR_VISUALIZATION_PROMPT,
} from "../../shared/assistant-visualization-prompts";
import {
  listAssistantCapabilitiesForContext,
  normalizeAssistantPersona,
  resolveAssistantModuleFromPath,
  type AssistantCapabilityCatalogScope,
  type AssistantCapabilityDefinition,
  type AssistantCapabilityType,
  type AssistantModuleKey,
  type AssistantPersona,
} from "./capability-matrix";
import { YSTNJ_ASSISTANT_PROMPT_MATRIX_SNAPSHOT } from "./prompt-matrix-snapshot";
import {
  ASSISTANT_ROUTE_FAMILIES,
  resolveAssistantRouteFamily,
} from "./route-family-registry";
import { getAssistantRuntimePolicy } from "./runtime-access-policy";
import {
  normalizeAssistantRouteIdentity,
  routeMatchesAnyAssistantPattern,
  stripAssistantRoutePatternQualifier,
} from "./route-matching";

export const ASSISTANT_TOOL_KINDS = [
  "provider.native",
  "provider.bedrock",
  "custom.augmented",
  "custom.codified",
  "custom.interpreter",
] as const;
export type AssistantToolKind = (typeof ASSISTANT_TOOL_KINDS)[number];

export const ASSISTANT_TOOL_EXECUTION_PLANES = [
  "supervisor_query",
  "ui_action",
  "async_task",
] as const;
export type AssistantToolExecutionPlane =
  (typeof ASSISTANT_TOOL_EXECUTION_PLANES)[number];

export const ASSISTANT_TOOL_VISIBILITY_VALUES = ["visible", "hidden"] as const;
export type AssistantToolVisibility =
  (typeof ASSISTANT_TOOL_VISIBILITY_VALUES)[number];

export const ASSISTANT_TOOL_PROMOTION_VALUES = [
  "promoted",
  "available",
  "hidden",
] as const;
export type AssistantToolPromotion =
  (typeof ASSISTANT_TOOL_PROMOTION_VALUES)[number];

export type AssistantToolDefinition = {
  toolId: string;
  title: string;
  summary: string;
  toolKind: AssistantToolKind;
  executionPlane: AssistantToolExecutionPlane;
  module: AssistantModuleKey;
  routePrefixes: string[];
  personas: AssistantPersona[];
  visibility: AssistantToolVisibility;
  promotion: AssistantToolPromotion;
  prompt: string | null;
  requiresConfirmation: boolean;
  executorRef: string | null;
  providerToolName: string | null;
  capabilityId: string | null;
  capabilityType: AssistantCapabilityType | null;
  actionKey: string | null;
  workflowKey: string | null;
  taskClass:
    | "artifact_generate"
    | "compute_report"
    | "draft_then_approve"
    | "external_side_effect"
    | null;
  page: string | null;
  tab: string | null;
  sequence: number | null;
  method: string | null;
  targetRoute: string | null;
  sourceOrigin: string | null;
  scenarioKey: string | null;
  likelySources: string[];
  dataStateSignals: string[];
  groupLabel: "Insights" | "Actions" | "Custom Tools" | "Internal";
  supportsProactivePromotion: boolean;
  userVisible: boolean;
  supervisorToolDescription: string | null;
  supervisorInputSchema: Record<string, unknown> | null;
};

type AssistantToolContext = {
  pathname?: string | null;
  persona?: string | null;
  module?: AssistantModuleKey | null;
  limit?: number;
  catalogScope?: AssistantCapabilityCatalogScope;
};

const SNAPSHOT_BY_CAPABILITY_ID = new Map(
  YSTNJ_ASSISTANT_PROMPT_MATRIX_SNAPSHOT.map(
    (entry) => [entry.id, entry] as const,
  ),
);

const STAFF_TOOL_PERSONAS: AssistantPersona[] = [
  "staff",
  "admin",
  "owner",
  "editor",
  "viewer",
  "nurse",
  "super-administrator",
];

const routeMatchesAnyPrefix = (
  pathname: string,
  prefixes: string[],
): boolean => {
  return routeMatchesAnyAssistantPattern(pathname, prefixes);
};

const dedupeByToolId = (
  tools: AssistantToolDefinition[],
): AssistantToolDefinition[] => {
  const deduped = new Map<string, AssistantToolDefinition>();
  for (const tool of tools) {
    deduped.set(tool.toolId, tool);
  }
  return Array.from(deduped.values());
};

const promotionScore = (promotion: AssistantToolPromotion): number => {
  switch (promotion) {
    case "promoted":
      return 3;
    case "available":
      return 2;
    default:
      return 1;
  }
};

const planeScore = (plane: AssistantToolExecutionPlane): number => {
  switch (plane) {
    case "async_task":
      return 3;
    case "ui_action":
      return 2;
    default:
      return 1;
  }
};

const moduleRouteScore = (
  tool: AssistantToolDefinition,
  pathname: string,
  module: AssistantModuleKey,
): number =>
  (routeMatchesAnyPrefix(pathname, tool.routePrefixes) ? 10 : 0) +
  (tool.module === module ? 5 : 0);

const sortTools = (
  tools: AssistantToolDefinition[],
  pathname: string,
  module: AssistantModuleKey,
): AssistantToolDefinition[] =>
  [...tools].sort(
    (left, right) =>
      moduleRouteScore(right, pathname, module) -
        moduleRouteScore(left, pathname, module) ||
      promotionScore(right.promotion) - promotionScore(left.promotion) ||
      planeScore(right.executionPlane) - planeScore(left.executionPlane) ||
      Number(left.sequence ?? Number.MAX_SAFE_INTEGER) -
        Number(right.sequence ?? Number.MAX_SAFE_INTEGER) ||
      left.title.localeCompare(right.title),
  );

const normalizeToolRouteSuffix = (pathname: string): string =>
  normalizeAssistantRouteIdentity(pathname)
    .replace(/^\/+/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const capabilityToTool = (
  capability: AssistantCapabilityDefinition,
): AssistantToolDefinition => {
  const snapshot = SNAPSHOT_BY_CAPABILITY_ID.get(capability.id);
  return {
    toolId: `capability:${capability.id}`,
    title: capability.title,
    summary: capability.rationale,
    toolKind: "custom.codified",
    executionPlane: "ui_action",
    module: capability.module,
    routePrefixes: [...capability.routePrefixes],
    personas: [...capability.personas],
    visibility: "visible",
    promotion: capability.type === "insight" ? "promoted" : "available",
    prompt: capability.prompt,
    requiresConfirmation: Boolean(capability.requiresConfirmation),
    executorRef:
      capability.type === "action"
        ? "assistantPlatform.executeAssistantAction"
        : "assistantPlatform.askSchoolAI",
    providerToolName: null,
    capabilityId: capability.id,
    capabilityType: capability.type,
    actionKey: capability.actionKey ?? null,
    workflowKey: null,
    taskClass: null,
    page: capability.page,
    tab: capability.tab,
    sequence: capability.sequence,
    method: snapshot?.method ?? capability.method ?? null,
    targetRoute:
      stripAssistantRoutePatternQualifier(
        capability.routePrefixes[0] ?? null,
      ) || null,
    sourceOrigin: snapshot?.sourceOrigin ?? null,
    scenarioKey: snapshot?.scenarioKey ?? null,
    likelySources: Array.isArray(snapshot?.likelySources)
      ? [...snapshot.likelySources]
      : [],
    dataStateSignals: Array.isArray(snapshot?.dataStateSignals)
      ? [...snapshot.dataStateSignals]
      : [],
    groupLabel: capability.type === "action" ? "Actions" : "Insights",
    supportsProactivePromotion: capability.type === "insight",
    userVisible: true,
    supervisorToolDescription: null,
    supervisorInputSchema: null,
  };
};

const asyncTaskTool = (input: {
  toolId: string;
  title: string;
  summary: string;
  prompt: string;
  module: AssistantModuleKey;
  routePrefixes: string[];
  workflowKey: string;
  taskClass: AssistantToolDefinition["taskClass"];
  requiresConfirmation?: boolean;
  promotion?: AssistantToolPromotion;
  targetRoute?: string | null;
}): AssistantToolDefinition => ({
  toolId: input.toolId,
  title: input.title,
  summary: input.summary,
  toolKind: "custom.interpreter",
  executionPlane: "async_task",
  module: input.module,
  routePrefixes: [...input.routePrefixes],
  personas: [...STAFF_TOOL_PERSONAS],
  visibility: "visible",
  promotion: input.promotion ?? "available",
  prompt: input.prompt,
  requiresConfirmation: Boolean(input.requiresConfirmation),
  executorRef: "assistantPlatform.mutateAssistantRun",
  providerToolName: null,
  capabilityId: null,
  capabilityType: null,
  actionKey: null,
  workflowKey: input.workflowKey,
  taskClass: input.taskClass,
  page: null,
  tab: null,
  sequence: null,
  method: null,
  targetRoute:
    (input.targetRoute ??
      stripAssistantRoutePatternQualifier(input.routePrefixes[0] ?? null)) ||
    null,
  sourceOrigin: "assistant-tool-registry",
  scenarioKey: null,
  likelySources: [],
  dataStateSignals: [],
  groupLabel: "Custom Tools",
  supportsProactivePromotion: true,
  userVisible: true,
  supervisorToolDescription: null,
  supervisorInputSchema: null,
});

const resolveVisualization3DToolCopy = (routeFamilyId: string): {
  title: string;
  summary: string;
  prompt: string;
} => {
  if (routeFamilyId === "tuition" || routeFamilyId === "assistant-home") {
    return {
      title: "Tuition prior-year 3D visualization",
      summary:
        "Generate an interactive Three.js tuition payment artifact with current-year and prior-year layers.",
      prompt: TUITION_PRIOR_YEAR_VISUALIZATION_PROMPT,
    };
  }
  return {
    title: "Advanced Three.js visualization",
    summary:
      "Generate an interactive Three.js admissions artifact for the current dashboard window.",
    prompt: ADMISSIONS_THREEJS_VISUALIZATION_PROMPT,
  };
};

const buildArtifactTaskTools = (input: {
  pathname: string;
  persona: string;
  module: AssistantModuleKey;
}): AssistantToolDefinition[] => {
  const routeFamily = resolveAssistantRouteFamily(input.pathname);
  const tools: AssistantToolDefinition[] = [];
  const routePrefixes =
    routeFamily.routePrefixes.length > 0
      ? [...routeFamily.routePrefixes]
      : [input.pathname];
  const visualization3DCopy = resolveVisualization3DToolCopy(routeFamily.id);

  const chartSupport = resolveAssistantArtifactSupport({
    pathname: input.pathname,
    workflowKey: "artifact.chart.generate",
    question:
      "Make a simple bar chart categorized by status using a Recharts diagram.",
  });
  const visualization3DSupport = resolveAssistantArtifactSupport({
    pathname: input.pathname,
    workflowKey: "artifact.visualization3d.generate",
    question: visualization3DCopy.prompt,
  });
  if (visualization3DSupport.supported) {
    tools.push(
      asyncTaskTool({
        toolId: "tool:artifact.visualization3d.generate",
        title: visualization3DCopy.title,
        summary: visualization3DCopy.summary,
        prompt: visualization3DCopy.prompt,
        module: routeFamily.module,
        routePrefixes,
        workflowKey: "artifact.visualization3d.generate",
        taskClass: "artifact_generate",
        promotion: "promoted",
        targetRoute: input.pathname,
      }),
    );
  }
  if (chartSupport.supported) {
    tools.push(
      asyncTaskTool({
        toolId: "tool:artifact.chart.generate",
        title: "Create status chart",
        summary:
          "Generate a Recharts-ready chart artifact for the current route.",
        prompt:
          "Make a simple bar chart categorized by status using a Recharts diagram.",
        module: routeFamily.module,
        routePrefixes,
        workflowKey: "artifact.chart.generate",
        taskClass: "artifact_generate",
        promotion: "promoted",
        targetRoute: input.pathname,
      }),
    );
  }

  const spreadsheetSupport = resolveAssistantArtifactSupport({
    pathname: input.pathname,
    workflowKey: "artifact.spreadsheet.generate",
    question: "Generate a spreadsheet export for the current route.",
  });
  if (spreadsheetSupport.supported) {
    tools.push(
      asyncTaskTool({
        toolId: "tool:artifact.spreadsheet.generate",
        title: "Export spreadsheet",
        summary: "Build a spreadsheet artifact from the current route context.",
        prompt: "Generate a spreadsheet export for the current route.",
        module: routeFamily.module,
        routePrefixes,
        workflowKey: "artifact.spreadsheet.generate",
        taskClass: "artifact_generate",
        promotion: "promoted",
        targetRoute: input.pathname,
      }),
    );
  }

  if (input.persona !== "parent" && routeFamily.id !== "unknown") {
    tools.push(
      asyncTaskTool({
        toolId: "tool:artifact.diagram.generate",
        title: "Generate diagram",
        summary:
          "Draft a Mermaid-style workflow diagram for the current route context.",
        prompt: `Generate a Mermaid diagram for the ${routeFamily.label} workflow.`,
        module: routeFamily.module,
        routePrefixes,
        workflowKey: "artifact.diagram.generate",
        taskClass: "artifact_generate",
        targetRoute: input.pathname,
      }),
    );
  }

  if (
    routeFamily.id === "donations" ||
    routeFamily.id === "camps" ||
    routeFamily.id === "settings"
  ) {
    tools.push(
      asyncTaskTool({
        toolId: "tool:artifact.landing_page.draft",
        title: "Draft landing page",
        summary:
          "Generate a standalone landing-page artifact in the background.",
        prompt: "Draft a landing page for the current route context.",
        module: routeFamily.module,
        routePrefixes,
        workflowKey: "artifact.landing_page.draft",
        taskClass: "artifact_generate",
        targetRoute: input.pathname,
      }),
    );
  }

  if (routeFamily.id === "finance" || routeFamily.id === "students-families") {
    tools.push(
      asyncTaskTool({
        toolId: "tool:finance.report.pending_payment_revenue",
        title: "Pending-payment report",
        summary: "Calculate and package a pending-payment revenue report.",
        prompt: "Build a pending-payment revenue report for the current route.",
        module: routeFamily.module,
        routePrefixes,
        workflowKey: "finance.report.pending_payment_revenue",
        taskClass: "compute_report",
        targetRoute: input.pathname,
      }),
    );
    tools.push(
      asyncTaskTool({
        toolId: "tool:finance.invoice.send",
        title: "Send invoice",
        summary:
          "Prepare an invoice task and require explicit confirmation before delivery.",
        prompt: "Send an invoice for the current family or finance context.",
        module: routeFamily.module,
        routePrefixes,
        workflowKey: "finance.invoice.send",
        taskClass: "external_side_effect",
        requiresConfirmation: true,
        targetRoute: input.pathname,
      }),
    );
  }

  return tools;
};

const buildStaffGlobalArtifactTaskTools = (input: {
  persona: string;
}): AssistantToolDefinition[] => {
  const tools: AssistantToolDefinition[] = [];
  for (const routeFamily of ASSISTANT_ROUTE_FAMILIES) {
    if (
      routeFamily.id === "assistant-home" ||
      routeFamily.id === "unknown" ||
      routeFamily.id === "parent-portal"
    ) {
      continue;
    }
    for (const route of routeFamily.suggestedRoutes) {
      const targetPathname = route.path;
      const runtimePolicy = getAssistantRuntimePolicy({
        pathname: targetPathname,
        persona: input.persona,
      });
      if (!runtimePolicy.enabled) {
        continue;
      }
      const routeTools = buildArtifactTaskTools({
        pathname: targetPathname,
        persona: input.persona,
        module: routeFamily.module,
      });
      for (const tool of routeTools) {
        const routeSuffix = normalizeToolRouteSuffix(targetPathname);
        tools.push({
          ...tool,
          toolId: `${tool.toolId}:${routeSuffix}`,
          title: `${routeFamily.label}: ${tool.title}`,
          summary: tool.summary.replace("current route", routeFamily.label),
          module: routeFamily.module,
          routePrefixes: [...routeFamily.routePrefixes],
          targetRoute: targetPathname,
          sourceOrigin: "assistant-tool-registry:staff-global",
        });
      }
    }
  }
  return tools;
};

export const ASSISTANT_SUPERVISOR_QUERY_TOOLS: AssistantToolDefinition[] = [
  {
    toolId: "supervisor:knowledge_search",
    title: "Knowledge Search",
    summary:
      "Retrieve tenant-filtered product, schema, policy, and operations context.",
    toolKind: "custom.augmented",
    executionPlane: "supervisor_query",
    module: "general",
    routePrefixes: [],
    personas: [...STAFF_TOOL_PERSONAS, "parent"],
    visibility: "hidden",
    promotion: "hidden",
    prompt: null,
    requiresConfirmation: false,
    executorRef: "intellioSupervisor.retrieveKnowledgeBaseContext",
    providerToolName: "knowledge_search",
    capabilityId: null,
    capabilityType: null,
    actionKey: null,
    workflowKey: null,
    taskClass: null,
    page: null,
    tab: null,
    sequence: null,
    method: null,
    targetRoute: null,
    sourceOrigin: "assistant-tool-registry",
    scenarioKey: null,
    likelySources: [],
    dataStateSignals: [],
    groupLabel: "Internal",
    supportsProactivePromotion: false,
    userVisible: false,
    supervisorToolDescription:
      "Retrieve tenant-filtered product, schema, policy, and operations context from the Intellio knowledge base.",
    supervisorInputSchema: {
      type: "object",
      properties: {
        query: { type: "string" },
        topK: { type: "integer", minimum: 1, maximum: 8 },
      },
      required: ["query"],
    },
  },
  {
    toolId: "supervisor:query_dynamo",
    title: "Dynamo Query",
    summary: "Run a read-only tenant-scoped DynamoDB query.",
    toolKind: "custom.augmented",
    executionPlane: "supervisor_query",
    module: "general",
    routePrefixes: [],
    personas: [...STAFF_TOOL_PERSONAS, "parent"],
    visibility: "hidden",
    promotion: "hidden",
    prompt: null,
    requiresConfirmation: false,
    executorRef: "intellioSupervisor.invokeJsonLambda:dynamo",
    providerToolName: "query_dynamo",
    capabilityId: null,
    capabilityType: null,
    actionKey: null,
    workflowKey: null,
    taskClass: null,
    page: null,
    tab: null,
    sequence: null,
    method: null,
    targetRoute: null,
    sourceOrigin: "assistant-tool-registry",
    scenarioKey: null,
    likelySources: [],
    dataStateSignals: [],
    groupLabel: "Internal",
    supportsProactivePromotion: false,
    userVisible: false,
    supervisorToolDescription: "Run a read-only tenant-scoped DynamoDB query.",
    supervisorInputSchema: {
      type: "object",
      properties: {
        tableName: { type: "string" },
        partiql: { type: "string" },
        sdkQuery: { type: "object" },
      },
    },
  },
  {
    toolId: "supervisor:query_rds",
    title: "RDS Query",
    summary: "Run a read-only tenant-scoped PostgreSQL query.",
    toolKind: "custom.augmented",
    executionPlane: "supervisor_query",
    module: "general",
    routePrefixes: [],
    personas: [...STAFF_TOOL_PERSONAS, "parent"],
    visibility: "hidden",
    promotion: "hidden",
    prompt: null,
    requiresConfirmation: false,
    executorRef: "intellioSupervisor.invokeJsonLambda:rds",
    providerToolName: "query_rds",
    capabilityId: null,
    capabilityType: null,
    actionKey: null,
    workflowKey: null,
    taskClass: null,
    page: null,
    tab: null,
    sequence: null,
    method: null,
    targetRoute: null,
    sourceOrigin: "assistant-tool-registry",
    scenarioKey: null,
    likelySources: [],
    dataStateSignals: [],
    groupLabel: "Internal",
    supportsProactivePromotion: false,
    userVisible: false,
    supervisorToolDescription:
      "Run a read-only tenant-scoped PostgreSQL query.",
    supervisorInputSchema: {
      type: "object",
      properties: {
        sql: { type: "string" },
        parameters: { type: "array" },
      },
      required: ["sql"],
    },
  },
  {
    toolId: "supervisor:aggregate_results",
    title: "Aggregate Results",
    summary: "Aggregate prior tool datasets with deterministic operations.",
    toolKind: "custom.interpreter",
    executionPlane: "supervisor_query",
    module: "general",
    routePrefixes: [],
    personas: [...STAFF_TOOL_PERSONAS, "parent"],
    visibility: "hidden",
    promotion: "hidden",
    prompt: null,
    requiresConfirmation: false,
    executorRef: "intellioSupervisor.invokeJsonLambda:aggregate",
    providerToolName: "aggregate_results",
    capabilityId: null,
    capabilityType: null,
    actionKey: null,
    workflowKey: null,
    taskClass: null,
    page: null,
    tab: null,
    sequence: null,
    method: null,
    targetRoute: null,
    sourceOrigin: "assistant-tool-registry",
    scenarioKey: null,
    likelySources: [],
    dataStateSignals: [],
    groupLabel: "Internal",
    supportsProactivePromotion: false,
    userVisible: false,
    supervisorToolDescription:
      "Aggregate prior tool datasets with deterministic operations.",
    supervisorInputSchema: {
      type: "object",
      properties: {
        datasets: { type: "array" },
        operation: { type: "string" },
        field: { type: "string" },
        groupByField: { type: "string" },
      },
      required: ["datasets"],
    },
  },
];

export const listAssistantSupervisorQueryTools = (
  _module: AssistantModuleKey,
): AssistantToolDefinition[] => [...ASSISTANT_SUPERVISOR_QUERY_TOOLS];

export const listAssistantToolsForContext = (
  input: AssistantToolContext,
): AssistantToolDefinition[] => {
  const pathname = normalizeAssistantRouteIdentity(input.pathname);
  const persona = normalizeAssistantPersona(input.persona);
  const module = input.module ?? resolveAssistantModuleFromPath(pathname);
  const catalogScope = input.catalogScope ?? "route";
  const runtimePolicy = getAssistantRuntimePolicy({
    pathname,
    persona,
  });

  if (!runtimePolicy.enabled) {
    return [];
  }

  const capabilityTools = listAssistantCapabilitiesForContext({
    pathname,
    persona,
    module,
    catalogScope,
    strictRouteMatch: catalogScope === "route",
  }).map(capabilityToTool);

  const asyncTools = catalogScope === "staff-global"
    ? buildStaffGlobalArtifactTaskTools({ persona })
    : buildArtifactTaskTools({
        pathname,
        persona,
        module,
      });

  const visibleTools = dedupeByToolId(
    capabilityTools.concat(asyncTools),
  ).filter((tool) => tool.userVisible && tool.visibility === "visible");

  const sorted = sortTools(visibleTools, pathname, module);
  if (!input.limit || sorted.length <= input.limit) {
    return sorted;
  }
  return sorted.slice(0, input.limit);
};

export type AssistantCapabilityProjection = {
  id: string;
  page: string;
  tab: string;
  sequence: number;
  title: string;
  type: AssistantCapabilityType;
  module: AssistantModuleKey;
  prompt: string;
  rationale: string;
  method: string | null;
  requiresConfirmation: boolean;
  actionKey: string | null;
  targetRoute: string | null;
  sourceOrigin: string | null;
  scenarioKey: string | null;
  likelySources: string[];
  routePrefixes: string[];
  dataStateSignals: string[];
};

export const listAssistantCapabilityProjectionForContext = (
  input: AssistantToolContext,
): AssistantCapabilityProjection[] =>
  listAssistantToolsForContext(input)
    .filter((tool) => tool.capabilityId && tool.capabilityType && tool.prompt)
    .map((tool) => ({
      id: tool.capabilityId as string,
      page: tool.page ?? "",
      tab: tool.tab ?? "",
      sequence: tool.sequence ?? 0,
      title: tool.title,
      type: tool.capabilityType as AssistantCapabilityType,
      module: tool.module,
      prompt: tool.prompt as string,
      rationale: tool.summary,
      method: tool.method,
      requiresConfirmation: tool.requiresConfirmation,
      actionKey: tool.actionKey,
      targetRoute: tool.targetRoute,
      sourceOrigin: tool.sourceOrigin,
      scenarioKey: tool.scenarioKey,
      likelySources: [...tool.likelySources],
      routePrefixes: [...tool.routePrefixes],
      dataStateSignals: [...tool.dataStateSignals],
    }));
